import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { portalDs, PortalDsAlert } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import { useGetOrganizationClientsQuery } from 'src/pages/client-management/endpoints/client.endpoints';
import { CampaignRequestDrawer } from './components/campaign-request-drawer';
import { CampaignsTable } from './components/campaigns-table';
import './endpoints/launched-campaigns.endpoints';
import { useLaunchedCampaigns } from './hooks/use-launched-campaigns';
import type { CampaignStatusFilter } from './types/campaigns.types';
import { CAMPAIGN_STATUS_OPTIONS } from './utils/campaign-status.utils';

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  ...CAMPAIGN_STATUS_OPTIONS,
] as const;

export function CampaignsTab() {
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const {
    campaigns,
    totalCount,
    page,
    pageSize,
    setPage,
    statusFilter,
    setStatusFilter,
    runAction,
    assignClient,
    isLoading,
    isError,
    isUpdating,
  } = useLaunchedCampaigns();

  const { data: clients = [] } = useGetOrganizationClientsQuery();

  const handleRowClick = (campaignId: string) => {
    setSelectedCampaignId(campaignId);
    setDrawerOpen(true);
  };

  const handleDrawerOpenChange = (open: boolean) => {
    setDrawerOpen(open);
    if (!open) {
      setSelectedCampaignId(null);
    }
  };

  return (
    <div className={cn(portalDs.page, 'h-full overflow-y-auto px-6 py-8 sm:px-10')}>
      <div className="mb-6">
        <h1 className="text-[20px] font-semibold tracking-[-0.02em] text-[#0D0F1A]">
          Requests
        </h1>
        <p className="mt-1 text-[14px] text-[#5E6278]">
          Campaigns launched from the self-service app. Assign a client when needed and review Monday and Slack sync status.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-3 rounded-xl border border-[#EDEEF2] bg-white p-4">
        <label className={cn(portalDs.field, 'min-w-[160px]')}>
          <span className={portalDs.filterLabel}>Status</span>
          <select
            className={portalDs.select}
            value={statusFilter}
            disabled={isLoading || isUpdating}
            onChange={(event) => setStatusFilter(event.target.value as CampaignStatusFilter)}
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-[#5E6278]">
          <Loader2
            className="size-5 animate-spin"
            aria-hidden
          />
          <span>
            Loading requests…
          </span>
        </div>
      ) : isError ? (
        <PortalDsAlert
          variant="danger"
          title="Could not load requests"
        >
          Campaign requests are temporarily unavailable. Try refreshing the page.
        </PortalDsAlert>
      ) : (
        <CampaignsTable
          campaigns={campaigns}
          clients={clients}
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onAction={runAction}
          onAssignClient={assignClient}
          onRowClick={handleRowClick}
          actionsDisabled={isUpdating}
        />
      )}

      <CampaignRequestDrawer
        campaignId={selectedCampaignId}
        open={drawerOpen}
        onOpenChange={handleDrawerOpenChange}
        onAction={runAction}
        actionsDisabled={isUpdating}
      />
    </div>
  );
}
